import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import classNames from 'classnames';
import api from 'api';
import { useNavigate } from 'react-router-dom';
import useSearchParams from 'hooks/useSearchParams';
import ReorderResizeTable from 'components/ReorderResizeTable';
import {
  BODY_CONTENT_GENERATE,
  NudgeCellComponent,
} from './components/BuyerDealTableCells';
import {
  HEADING_TRANSLATIONS,
  SORTING_VALUES,
  TABLE_COLUMNS,
} from './components/BuyerDealTableCells/data';
import { PortfoilioLead } from 'api/portfolio/buyer';
import { CUSTOM_COL_SIZES } from '../TableCells/data';
import EditColumnsVisibilityButton from 'components/EditColumnsVisibilityButton';
import ResolvePagination from 'components/ResolvePagination';
import ListingPortfolioCard from 'components/ListingPortfolioCard';
import PortfolioPlaceholderCards from '../../../../components/PortfolioPlaceholderCards';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import CurrentUserContext from 'providers/CurrentUser/CurrentUser.context';
import usePageParams from 'hooks/usePageParams';

import './BuyerDeals.styles.scss';

type BuyerDealsProps = {
  className?: string;
  dataLimit: string;
  isEmpty?: boolean;
  isTableView?: boolean;
  openManageList: () => void;
  setIsEmptyDeals: (value: boolean) => void;
  refetch: () => void;
};

const BuyerDeals: React.FC<BuyerDealsProps> = (props) => {
  const {
    className,
    dataLimit,
    isEmpty,
    isTableView,
    setIsEmptyDeals,
    openManageList,
    refetch,
  } = props;

  const classes = classNames('buyer-deals', 'resolve-page-items', className);

  const [totalPages, setTotalPages] = useState(0);
  const [isBuyerDealsLoading, setBuyerDealsIsLoading] = useState(true);
  const [leads, setLeads] = useState<PortfoilioLead[]>([]);
  const [orderedColumns, setOrderedColumns] = useState(TABLE_COLUMNS);

  const { popServerError } = useContext(PopNotificationsContext);
  const {
    currentUser: { hideBuyerPortfolioColumns },
  } = useContext(CurrentUserContext);

  const page = usePageParams();

  const searchParams = useSearchParams();
  const navigate = useNavigate();

  const tableColumns = useMemo(
    () =>
      orderedColumns.filter((el) => !hideBuyerPortfolioColumns.includes(el)),
    [hideBuyerPortfolioColumns, orderedColumns],
  );

  const onReorderL = useCallback((c) => setOrderedColumns(c), []);

  const onSubmitSuccess = useCallback(refetch, [refetch]);

  const renderedContent = useMemo(
    () =>
      orderedColumns.reduce(
        (acc, col) => ({
          ...acc,
          [col]: (lead: PortfoilioLead) =>
            BODY_CONTENT_GENERATE[col]({
              ...lead,
              config: {
                buyerStep: lead.buyerStep,
                withdrawnInterest: lead.withdrawnInterest,
                confirmedInterest: lead.confirmedInterest,
                leadId: lead._id,
                dealId: lead.dealRoom?._id,
                dealName: lead.dealRoom?.name,
                onSubmitSuccess,
              },
            }),
        }),
        {},
      ),
    [onSubmitSuccess, orderedColumns],
  );

  const updateLead = useCallback(
    (lead: Partial<PortfoilioLead>) =>
      setLeads((old) =>
        old.map((el) => (el._id === lead._id ? { ...el, ...lead } : el)),
      ),
    [],
  );

  const renderPortfolioLeads = useMemo(
    () =>
      isBuyerDealsLoading ? (
        <PortfolioPlaceholderCards numberOfCards={+dataLimit} />
      ) : (
        leads.map(
          ({
            _id: leadId,
            dealRoom: {
              _id,
              name,
              stage,
              listingStage,
              publishedOn,
              industry,
              dealOutcome,
              otherDealOutcome,
              portfolioName,
              isColleague,
              userId,
              user,
              isRWC,
              leadStagesCopies,
            },
            buyerStep,
            withdrawnInterest,
            confirmedInterest,
            progressedStep: leadProgressedStep,
            listingEnquiry: {
              _id: enquiryId,
              numberOfAcceptedLeads,
              numberOfConfirmedLeads,
              numberOfEnquiredLeads,
              numberOfOfferedLeads,
              progressedStep,
            },
            listing,
            listing: { type, expirationDate, seekingExpressionOfInterestDate },
          }) => (
            <ListingPortfolioCard
              asBuyer
              key={_id}
              title={portfolioName}
              dealStage={stage}
              listingStage={listingStage}
              date={publishedOn}
              isColleague={isColleague}
              dealOutcome={dealOutcome}
              otherDealOutcome={otherDealOutcome}
              dealRoomId={_id}
              dealName={name}
              listingType={type}
              industry={industry}
              seekingExpressionOfInterestDate={seekingExpressionOfInterestDate}
              expirationDate={expirationDate}
              ownerId={userId}
              owner={user}
              enquiryId={enquiryId}
              buyerStep={buyerStep}
              withdrawnInterest={withdrawnInterest}
              confirmedInterest={confirmedInterest}
              numberOfAcceptedLeads={numberOfAcceptedLeads}
              numberOfConfirmedLeads={numberOfConfirmedLeads}
              numberOfEnquiredLeads={numberOfEnquiredLeads}
              numberOfOfferedLeads={numberOfOfferedLeads}
              progressedStep={progressedStep}
              leadProgressedStep={leadProgressedStep}
              leadId={leadId}
              listing={listing}
              isRWC={isRWC}
              onWithdrawSuccess={refetch}
              leadStagesCopies={leadStagesCopies}
            />
          ),
        )
      ),
    [dataLimit, isBuyerDealsLoading, leads, refetch],
  );

  useEffect(() => {
    if (!searchParams.$or) return;

    (async () => {
      try {
        setBuyerDealsIsLoading(true);

        const {
          data: { totalPages, items },
        } = await api.dealRoom.getPortfolioLeads({
          $or: searchParams.$or,
          $sort: searchParams.$sort,
          $page: page,
          $limit: dataLimit,
          $relations:
            '["seller","listing","listingEnquiry","dealRoom","nudgeNotification"]',
          timeframe: searchParams.timeframe,
        });

        setTotalPages(totalPages);
        setLeads(items);
        setIsEmptyDeals(!items.length);
      } catch (e) {
        popServerError(e);
      } finally {
        setBuyerDealsIsLoading(false);
      }
    })();
  }, [
    isTableView,
    searchParams?.$or,
    searchParams?.$sort,
    searchParams?.timeframe,
    page,
    dataLimit,
    popServerError,
    setIsEmptyDeals,
  ]);

  if (isEmpty) return null;

  return (
    <div className={classes}>
      {!isEmpty &&
        (!isTableView ? (
          <>
            {renderPortfolioLeads}
            <ResolvePagination totalPages={totalPages} />
          </>
        ) : (
          <>
            <EditColumnsVisibilityButton onClick={openManageList} />
            <ReorderResizeTable
              className="portfolio-results-table"
              headingTranslations={HEADING_TRANSLATIONS}
              tableContentTransformations={renderedContent}
              columns={tableColumns as Array<keyof PortfoilioLead>}
              sortingValues={SORTING_VALUES}
              isDataLoading={isBuyerDealsLoading}
              data={leads}
              customSizes={CUSTOM_COL_SIZES}
              totalPages={totalPages}
              getIsRowExpanded={({ original }) => !!original.nudgeNotification}
              onRowClick={(lead) => navigate(lead.listingEnquiry._id)}
              onReorder={onReorderL}
              renderExpandedContent={(lead) => (
                <NudgeCellComponent
                  _id={lead._id}
                  nudgeNotification={lead.nudgeNotification}
                  config={{ onSubmitSuccess, updateLead }}
                />
              )}
            />
          </>
        ))}
    </div>
  );
};

export default BuyerDeals;
